import NightlightIcon from "@mui/icons-material/Nightlight";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { SURFACE } from "../../../theme/tokens";
import { ROLE_GROUPS, RoleGroup, ShiftFilter } from "../types";

// Functional / Categorical: day vs night shift indicator colors. Must stay stable across themes.
const SHIFT_INDICATOR_BG = {
	DAY: "#FB8C00",
	NIGHT: "var(--inhouse-primary-dark)",
} as const;

const SHIFT_OPTIONS: { value: ShiftFilter; label: string; icon: React.ReactNode; selectedBg: string }[] = [
	{ value: ShiftFilter.Day, label: "Day", icon: <WbSunnyIcon fontSize="small" />, selectedBg: SHIFT_INDICATOR_BG.DAY },
	{ value: ShiftFilter.Night, label: "Night", icon: <NightlightIcon fontSize="small" />, selectedBg: SHIFT_INDICATOR_BG.NIGHT },
];

export interface HiringSimulateDialogProps {
	open: boolean;
	onClose: () => void;
	dates: DateTime[];
	weekNumbers: number[];
	onSimulate: (role: string, shiftFilter: ShiftFilter, nurseName: string, selectedDates: DateTime[]) => void;
}

export const HiringSimulateDialog = ({ open, onClose, dates, weekNumbers, onSimulate }: HiringSimulateDialogProps) => {
	const [role, setRole] = useState<string>(RoleGroup.Nurse);
	const [shiftFilter, setShiftFilter] = useState<ShiftFilter>(ShiftFilter.Day);
	const [nurseName, setNurseName] = useState("");
	const [selectedDateKeys, setSelectedDateKeys] = useState<Set<string>>(new Set());

	const selectedDates = useMemo(() => dates.filter((d) => selectedDateKeys.has(d.toISODate() ?? "")), [dates, selectedDateKeys]);

	const orderedWeeks = useMemo(
		() => [
			{ label: `Week ${weekNumbers[0] ?? 1}`, weekDates: dates.slice(0, 7) },
			{ label: `Week ${weekNumbers[1] ?? 2}`, weekDates: dates.slice(7, 14) },
		],
		[dates, weekNumbers],
	);

	const toggleDate = useCallback((date: DateTime) => {
		const key = date.toISODate() ?? "";
		setSelectedDateKeys((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	}, []);

	const handleSimulate = useCallback(() => {
		onSimulate(role, shiftFilter, nurseName, selectedDates);
		onClose();
		setRole(RoleGroup.Nurse);
		setShiftFilter(ShiftFilter.Day);
		setNurseName("");
		setSelectedDateKeys(new Set());
	}, [role, shiftFilter, nurseName, selectedDates, onSimulate, onClose]);

	const handleClose = useCallback(() => {
		onClose();
		setRole(RoleGroup.Nurse);
		setShiftFilter(ShiftFilter.Day);
		setNurseName("");
		setSelectedDateKeys(new Set());
	}, [onClose]);

	const canSubmit = selectedDates.length > 0 && nurseName.trim() !== "";

	return (
		<Dialog
			data-testid="hiring-simulate-dialog"
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			slotProps={{
				paper: { sx: { borderRadius: 2 } },
			}}
		>
			<DialogTitle sx={{ fontWeight: 700, fontSize: "1.125rem" }}>Simulate staff</DialogTitle>
			<DialogContent dividers sx={{ py: 2, display: "flex", flexDirection: "column", gap: 2.5 }}>
				<Box>
					<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}>
						Role Group
					</Typography>
					<ToggleButtonGroup
						data-testid="hiring-simulate-role-group"
						value={role}
						exclusive
						onChange={(_, v) => v != null && setRole(v)}
						size="small"
						fullWidth
					>
						{ROLE_GROUPS.map((r) => (
							<ToggleButton key={r} value={r} aria-label={r}>
								{r}
							</ToggleButton>
						))}
					</ToggleButtonGroup>
				</Box>

				<Box>
					<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}>
						Shift type
					</Typography>
					<ToggleButtonGroup
						data-testid="hiring-simulate-shift-type"
						value={shiftFilter}
						exclusive
						onChange={(_, v) => v != null && setShiftFilter(v)}
						size="small"
						fullWidth
					>
						{SHIFT_OPTIONS.map((opt) => (
							<ToggleButton
								key={opt.value}
								value={opt.value}
								aria-label={opt.label}
								sx={{
									gap: 0.75,
									"&.Mui-selected": {
										bgcolor: opt.selectedBg,
										color: SURFACE,
										borderColor: "transparent",
										"&:hover": { bgcolor: opt.selectedBg, opacity: 0.92 },
									},
								}}
							>
								{opt.icon}
								{opt.label}
							</ToggleButton>
						))}
					</ToggleButtonGroup>
				</Box>

				<TextField
					data-testid="hiring-simulate-nurse-name-input"
					label="Nurse Name"
					value={nurseName}
					onChange={(e) => setNurseName(e.target.value)}
					placeholder="e.g. Jane D."
					size="small"
					fullWidth
				/>

				<Box>
					<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: "text.secondary" }}>
						Select days — click to toggle
					</Typography>
					{orderedWeeks.map(({ label, weekDates }) => (
						<Box key={label} sx={{ mb: 2 }}>
							<Typography sx={{ fontWeight: 600, color: "text.secondary", display: "block", mb: 1 }}>{label}</Typography>
							<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
								{weekDates.map((date) => {
									const key = date.toISODate() ?? "";
									const selected = selectedDateKeys.has(key);
									const selectedBg = SHIFT_OPTIONS.find((o) => o.value === shiftFilter)?.selectedBg ?? "primary.main";
									return (
										<ToggleButton
											key={key}
											value={key}
											selected={selected}
											onClick={() => toggleDate(date)}
											size="small"
											sx={{
												minWidth: 36,
												py: 0.75,
												px: 1,
												borderRadius: 1.5,
												textTransform: "none",
												"&.Mui-selected": {
													bgcolor: selectedBg,
													color: SURFACE,
													borderColor: "transparent",
													"&:hover": { bgcolor: selectedBg, opacity: 0.92 },
												},
												"&:not(.Mui-selected)": {
													borderColor: "divider",
													color: "text.secondary",
												},
											}}
										>
											<Typography variant="caption" sx={{ fontSize: "0.7rem", lineHeight: 1.2, fontWeight: 500 }}>
												{date.toFormat("ccc")}
											</Typography>
										</ToggleButton>
									);
								})}
							</Box>
						</Box>
					))}
				</Box>
			</DialogContent>
			<DialogActions sx={{ px: 3, py: 2 }}>
				<Button data-testid="hiring-simulate-cancel-button" onClick={handleClose}>
					Cancel
				</Button>
				<Button data-testid="hiring-simulate-confirm-button" variant="contained" onClick={handleSimulate} disabled={!canSubmit}>
					Simulate
				</Button>
			</DialogActions>
		</Dialog>
	);
};
